/**
 * GraphQL mutations for Schedule primitive
 *
 * 6 mutations covering:
 * - Create schedule
 * - Pause/Resume/Disable schedule
 * - Trigger immediate execution
 * - Record execution results
 */

import { gql } from '@apollo/client';
import { SCHEDULE_FIELDS } from './fragments';

/**
 * Create a new schedule
 * Generates backend Snowflake ID
 */
export const CREATE_SCHEDULE = gql`
  ${SCHEDULE_FIELDS}
  mutation CreateSchedule($tenantId: String!, $input: CreateScheduleInput!) {
    createSchedule(tenantId: $tenantId, input: $input) {
      ...ScheduleFields
    }
  }
`;

/**
 * Pause an active schedule
 * Temporarily stops execution without disabling
 */
export const PAUSE_SCHEDULE = gql`
  ${SCHEDULE_FIELDS}
  mutation PauseSchedule($tenantId: String!, $scheduleId: String!) {
    pauseSchedule(tenantId: $tenantId, scheduleId: $scheduleId) {
      ...ScheduleFields
    }
  }
`;

/**
 * Resume a paused schedule
 * Restarts execution from where it was paused
 */
export const RESUME_SCHEDULE = gql`
  ${SCHEDULE_FIELDS}
  mutation ResumeSchedule($tenantId: String!, $scheduleId: String!) {
    resumeSchedule(tenantId: $tenantId, scheduleId: $scheduleId) {
      ...ScheduleFields
    }
  }
`;

/**
 * Disable a schedule permanently
 * Schedule will not execute until re-enabled
 */
export const DISABLE_SCHEDULE = gql`
  ${SCHEDULE_FIELDS}
  mutation DisableSchedule($tenantId: String!, $scheduleId: String!) {
    disableSchedule(tenantId: $tenantId, scheduleId: $scheduleId) {
      ...ScheduleFields
    }
  }
`;

/**
 * Trigger immediate execution of a schedule
 * Bypasses normal cron timing for manual triggering
 */
export const TRIGGER_SCHEDULE_NOW = gql`
  ${SCHEDULE_FIELDS}
  mutation TriggerScheduleNow($tenantId: String!, $scheduleId: String!) {
    triggerScheduleNow(tenantId: $tenantId, scheduleId: $scheduleId) {
      ...ScheduleFields
    }
  }
`;

/**
 * Record execution result for a schedule
 * Updates execution count, last execution time, and failure tracking
 */
export const RECORD_EXECUTION = gql`
  ${SCHEDULE_FIELDS}
  mutation RecordExecution(
    $tenantId: String!
    $scheduleId: String!
    $success: Boolean!
    $executionTimeMs: Int!
  ) {
    recordExecution(
      tenantId: $tenantId
      scheduleId: $scheduleId
      success: $success
      executionTimeMs: $executionTimeMs
    ) {
      ...ScheduleFields
    }
  }
`;
